import type { NewsItem } from '../../types/news'

export interface NewsCoverageCluster {
  id: string
  itemIds: string[]
  publisherCount: number
}

const STOP_WORDS = new Set(['a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from', 'has', 'in', 'is', 'it', 'its', 'of', 'on', 'or', 's', 'that', 'the', 'to', 'with'])
const WINDOW_MS = 96 * 60 * 60 * 1000
const EVENT_ACTIONS: Array<[string, RegExp]> = [
  ['departure', /\b(out|depart(?:s|ed|ure)?|leav(?:e|es|ing)|resign(?:s|ed|ation)?|step(?:s|ped)? down|oust(?:ed)?)\b/i],
  ['launch', /\b(launch(?:es|ed)?|release(?:s|d)?|introduc(?:e|es|ed)|debut(?:s|ed)?)\b/i],
  ['funding', /\b(fund(?:s|ed|ing)?|financ(?:e|es|ed|ing)|raise(?:s|d)?|invest(?:s|ed|ment)?)\b/i],
  ['deal', /\b(acquir(?:e|es|ed)|merg(?:e|es|ed)|partner(?:s|ed|ship)|agreement|deal)\b/i],
  ['policy', /\b(rule|law|policy|regulat(?:e|es|ed|ion)|ban|approval|permit)\b/i],
  ['build', /\b(build|building|construction|campus|capacity|expand(?:s|ed|ion))\b/i],
]
const ENTITY_ALIASES: Record<string, string[]> = {
  openai: ['openai'], anthropic: ['anthropic'], nvidia: ['nvidia'], microsoft: ['microsoft'], amazon: ['amazon', 'aws'], google: ['google', 'deepmind'], meta: ['meta'], xai: ['xai'], apple: ['apple'], tesla: ['tesla'], amd: ['amd'], intel: ['intel'], tsmc: ['tsmc'], oracle: ['oracle'], alibaba: ['alibaba'], deepseek: ['deepseek'], mistral: ['mistral'],
}

function titleTokens(title: string): Set<string> {
  return new Set(title.toLocaleLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter((token) => token.length > 2 && !STOP_WORDS.has(token)))
}

const eventActions = (value: string) => new Set(EVENT_ACTIONS.filter(([, pattern]) => pattern.test(value)).map(([name]) => name))
const eventEntities = (value: string) => { const normalized = ` ${value.toLocaleLowerCase().replace(/[^a-z0-9]+/g, ' ')} `; return new Set(Object.entries(ENTITY_ALIASES).filter(([, aliases]) => aliases.some((alias) => normalized.includes(` ${alias} `))).map(([name]) => name)) }
const overlaps = (left: Set<string>, right: Set<string>) => [...left].some((value) => right.has(value))

function isSameEvent(left: NewsItem, right: NewsItem): boolean {
  if (left.kind === 'ai-synthesis' || right.kind === 'ai-synthesis' || left.sourceId === right.sourceId) return false
  if (Math.abs(new Date(left.publishedAt).getTime() - new Date(right.publishedAt).getTime()) > WINDOW_MS) return false
  if (!left.categories.some((category) => right.categories.includes(category))) return false
  const a = titleTokens(left.title); const b = titleTokens(right.title)
  if (a.size < 4 || b.size < 4) return false
  const overlap = [...a].filter((token) => b.has(token)).length
  const union = new Set([...a, ...b]).size
  const jaccard = overlap / union
  const containment = overlap / Math.min(a.size, b.size)
  if (jaccard >= 0.62 || (overlap >= 5 && containment >= 0.78)) return true
  const leftText = `${left.title} ${left.summary ?? ''}`; const rightText = `${right.title} ${right.summary ?? ''}`
  return overlap >= 2 && overlaps(eventEntities(leftText), eventEntities(rightText)) && overlaps(eventActions(leftText), eventActions(rightText))
}

export function clusterNewsCoverage(items: NewsItem[]): Map<string, NewsCoverageCluster> {
  const parents = items.map((_, index) => index)
  const find = (index: number): number => parents[index] === index ? index : (parents[index] = find(parents[index]))
  const union = (left: number, right: number) => { const a = find(left); const b = find(right); if (a !== b) parents[b] = a }
  for (let left = 0; left < items.length; left += 1) {
    for (let right = left + 1; right < items.length; right += 1) if (isSameEvent(items[left], items[right])) union(left, right)
  }
  const groups = new Map<number, NewsItem[]>()
  items.forEach((item, index) => { const root = find(index); groups.set(root, [...(groups.get(root) ?? []), item]) })
  const result = new Map<string, NewsCoverageCluster>()
  for (const group of groups.values()) {
    if (group.length < 2) continue
    const itemIds = group.map((item) => item.id)
    const cluster = { id: `event-${[...itemIds].sort()[0]}`, itemIds, publisherCount: new Set(group.map((item) => item.sourceId)).size }
    group.forEach((item) => result.set(item.id, cluster))
  }
  return result
}
